import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchEmployees, updateEmployee } from "../redux/actions/employeeActions";
import Papa from "papaparse";
import {
  FiChevronLeft, FiChevronRight, FiSearch, FiDownload, FiEdit, FiFilter,
  FiUser, FiMail, FiPhone, FiCalendar, FiX, FiMapPin, FiClock
} from "react-icons/fi";
import EditEmployeeModal from "../pages/employee_management/EditEmployeeModal";
import ChangeStatusDropdown from "../pages/employee_management/ChangeStatusDropdown";

const Employee = () => {
  const dispatch = useDispatch();
  const { employees, loading } = useSelector((state) => state.employee);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");
  const [selectedWard, setSelectedWard] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showFilters, setShowFilters] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  const districts = [...new Set(employees.map((e) => e.district).filter(Boolean))];
  const municipalities = [...new Set(employees.map((e) => e.municipality).filter(Boolean))];
  const statuses = [...new Set(employees.map((e) => e.status).filter(Boolean))];

  const sortedEmployees = [...employees].sort((a, b) => {
    if (!sortField) return 0;
    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";
    if (sortOrder === "asc") return aValue > bValue ? 1 : -1;
    else return aValue < bValue ? 1 : -1;
  });

  const filteredEmployees = sortedEmployees.filter((emp) => {
    const term = searchTerm.toLowerCase();
    return (
      (!selectedDistrict || emp.district === selectedDistrict) &&
      (!selectedMunicipality || emp.municipality === selectedMunicipality) &&
      (!selectedStatus || emp.status === selectedStatus) &&
      ((emp?.name || "").toLowerCase().includes(term) ||
        (emp?.email || "").toLowerCase().includes(term) ||
        (emp?.phone || "").toLowerCase().includes(term))
    );
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = filteredEmployees.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleDownloadCSV = () => {
    const csvRows = [
      ["ID", "Employee Name", "Contact (Email/Phone)", "DOB", "DOJ", "Status"],
    ];

    filteredEmployees.forEach((emp) => {
      csvRows.push([
        emp.id,
        emp.name || "",
        `${emp.email || ""} | ${emp.phone || ""}`,
       
       
        emp.dob ? new Date(emp.dob).toLocaleDateString("en-IN") : "",
        emp.doj ? new Date(emp.doj).toLocaleDateString("en-IN") : "",
        emp.status || "",
      ]);
    });

    const csv = Papa.unparse(csvRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `Employees_Clean_Report_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const handleEditClick = (emp) => {
    setEditEmployee(emp);
    setIsEditOpen(true);
  };

  const handleFieldChange = (field, value) => {
    setEditEmployee((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateEmployee(editEmployee));
      setIsEditOpen(false);
      dispatch(fetchEmployees());
    } catch (error) {
      console.error("Error updating employee:", error);
    }
  };

  const clearFilters = () => {
    setSelectedDistrict("");
    setSelectedMunicipality("");
    setSelectedStatus("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  // NEW REDUCED COLUMN HEADERS
  const columns = [
    { key: "photo", label: "Photo", width: "60px" },
    { key: "name", label: "Employee Details", width: "220px" },
    { key: "email", label: "Contact Info", width: "200px" },
    { key: "status", label: "Status", width: "80px" },
    { key: "actions", label: "Action", width: "80px" },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Employee Directory</h1>
          <p className="text-gray-600 dark:text-gray-400">View and manage staff roles and schedules</p>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <button onClick={handleDownloadCSV} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl transition-all shadow-sm">
            <FiDownload /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, contact, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white transition-all"
            />
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-4 py-2.5 rounded-xl text-gray-600 dark:text-gray-300">
            <FiFilter /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100 dark:border-gray-700">
            
            <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="bg-gray-50 dark:bg-gray-700 border-none rounded-lg p-2.5 text-sm dark:text-white">
              <option value="">All Status</option>
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Main Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-gray-50/50 dark:bg-gray-700/30 border-b border-gray-100 dark:border-gray-700">
              {columns.map((col) => (
                <th 
                  key={col.key} 
                  className="p-4 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-blue-500 transition-colors"
                  style={{ width: col.width }}
                  onClick={() => !['photo', 'actions'].includes(col.key) && toggleSort(col.key)}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {sortField === col.key && (sortOrder === "asc" ? "↑" : "↓")}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {currentItems.map((emp) => (
              <tr key={emp.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/20 transition-colors">
                {/* Photo Column */}
                <td className="p-4">
                  <img
                    src={emp.photo || "https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg"}
                    className="h-12 w-12 rounded-full object-cover ring-2 ring-gray-100 dark:ring-gray-700"
                    alt="Profile"
                  />
                </td>

                {/* Group 1: Identity */}
                <td className="p-4">
                  <div className="font-bold text-gray-900 dark:text-white">{emp.name}</div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                    <FiMapPin className="text-gray-400" /> {emp.district || "No Location"}
                  </div>
                  <div className="text-[10px] text-gray-400 uppercase mt-0.5">ID: {emp.id}</div>
                </td>

                {/* Group 2: Contact */}
                <td className="p-4">
                  <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <FiMail className="text-blue-400 shrink-0" /> {emp.email}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <FiPhone className="text-green-400 shrink-0" /> {emp.phone}
                  </div>
                </td>
    

                {/* Group 5: Status (Narrow) */}
                <td className="p-4 text-center">
                  <ChangeStatusDropdown userId={emp.id} currentStatus={emp.status} />
                </td>

                {/* Group 6: Actions (Narrow) */}
                <td className="p-4">
                  <button onClick={() => handleEditClick(emp)} className="p-2 bg-gray-100 dark:bg-gray-700 hover:bg-blue-500 hover:text-white text-gray-600 dark:text-gray-300 rounded-lg transition-all">
                    <FiEdit size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {currentItems.length === 0 && (
          <div className="p-20 text-center text-gray-400">
            <FiUser size={48} className="mx-auto mb-4 opacity-20" />
            <p>No records match your criteria</p>
          </div>
        )}
      </div>

      {/* Pagination - Simplified UI */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6 px-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 disabled:opacity-30 shadow-sm"
          >
            <FiChevronLeft size={20} />
          </button>
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 disabled:opacity-30 shadow-sm"
          >
            <FiChevronRight size={20} />
          </button>
        </div>
      )}

      {isEditOpen && editEmployee && (
        <EditEmployeeModal
          employee={editEmployee}
          onChange={handleFieldChange}
          onCancel={() => setIsEditOpen(false)}
          onSubmit={handleUpdateSubmit}
        />
      )}
    </div>
  );
};

export default Employee;